import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { saveTranslations, loadTranslations } from "./translations"

const SpecialMenuEditor = () => {
  const { i18n } = useTranslation("global")
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [itTranslations, setItTranslations] = useState(null)
  const [enTranslations, setEnTranslations] = useState(null)

  // Special menu fields
  const [enabled, setEnabled] = useState(false)
  const [titleIt, setTitleIt] = useState("")
  const [titleEn, setTitleEn] = useState("")
  const [descriptionIt, setDescriptionIt] = useState("")
  const [descriptionEn, setDescriptionEn] = useState("")
  const [buttonTextIt, setButtonTextIt] = useState("")
  const [buttonTextEn, setButtonTextEn] = useState("")
  const [imageUrlIt, setImageUrlIt] = useState("")
  const [imageUrlEn, setImageUrlEn] = useState("")

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [itData, enData] = await Promise.all([
          loadTranslations("it"),
          loadTranslations("en"),
        ])

        if (
          !itData || !enData ||
          Array.isArray(itData) || Array.isArray(enData) ||
          typeof itData !== "object" || typeof enData !== "object" ||
          Object.keys(itData).length === 0 || Object.keys(enData).length === 0
        ) {
          throw new Error("Failed to load translations: received empty or invalid data. Try refreshing.")
        }

        setItTranslations(itData)
        setEnTranslations(enData)

        const itMenu = itData?.menuCarta?.specialMenu || {}
        const enMenu = enData?.menuCarta?.specialMenu || {}

        setEnabled(itMenu.enabled === "true")
        setTitleIt(itMenu.title || "")
        setTitleEn(enMenu.title || "")
        setDescriptionIt(itMenu.description || "")
        setDescriptionEn(enMenu.description || "")
        setButtonTextIt(itMenu.buttonText || "")
        setButtonTextEn(enMenu.buttonText || "")
        setImageUrlIt(itMenu.imageUrl || "")
        setImageUrlEn(enMenu.imageUrl || "")
      } catch (error) {
        console.error("[SpecialMenuEditor] Error loading:", error)
        setMessage({ type: "error", text: "Failed to load: " + error.message })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleImageUpload = async (file, language) => {
    if (!file) return
    if (!file.type.match(/^image\/(jpeg|webp)$/)) {
      setMessage({ type: "error", text: "Only JPG/JPEG and WebP files are allowed" })
      return
    }

    const ext = file.type === "image/webp" ? "webp" : "jpeg"
    const formData = new FormData()
    formData.append("image", file)
    const filename = language === "en" ? `special-menu-en.${ext}` : `special-menu.${ext}`
    formData.append("filename", filename)

    try {
      setMessage({ type: "loading", text: "Uploading image..." })

      let successCount = 0
      let lastError = null

      try {
        const response1 = await fetch("https://www.casadipinta.com/upload-image.php", {
          method: "POST",
          body: formData,
        })
        if (response1.ok) {
          const data = await response1.json()
          if (data.success) successCount++
        }
      } catch (error) {
        console.warn("Upload to www.casadipinta.com failed:", error)
        lastError = error
      }

      try {
        const response2 = await fetch("https://works.casadipinta.com/upload-image.php", {
          method: "POST",
          body: formData,
        })
        if (response2.ok) {
          const data = await response2.json()
          if (data.success) successCount++
        }
      } catch (error) {
        console.warn("Upload to works.casadipinta.com failed:", error)
        lastError = error
      }

      if (successCount > 0) {
        const imageUrl = `https://www.casadipinta.com/img/${filename}?v=${new Date().getTime()}`
        if (language === "it") {
          setImageUrlIt(imageUrl)
        } else {
          setImageUrlEn(imageUrl)
        }
        const msg = successCount === 2
          ? "Image uploaded successfully to both servers"
          : `Image uploaded successfully to ${successCount}/2 servers`
        setMessage({ type: "success", text: msg })
      } else {
        throw lastError || new Error("Upload failed on both servers")
      }
    } catch (error) {
      console.error("Upload error:", error)
      setMessage({ type: "error", text: error.message || "Failed to upload image" })
    }
  }

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!itTranslations || !enTranslations) {
      setMessage({ type: "error", text: "Translations not loaded." })
      return
    }

    setIsSaving(true)
    setMessage({ type: "loading", text: "Saving special menu..." })

    try {
      const updatedIt = JSON.parse(JSON.stringify(itTranslations))
      const updatedEn = JSON.parse(JSON.stringify(enTranslations))

      if (!updatedIt.menuCarta) updatedIt.menuCarta = {}
      if (!updatedEn.menuCarta) updatedEn.menuCarta = {}

      const enabledStr = enabled ? "true" : "false"

      updatedIt.menuCarta.specialMenu = {
        enabled: enabledStr,
        title: titleIt,
        description: descriptionIt,
        buttonText: buttonTextIt,
        imageUrl: imageUrlIt,
      }

      updatedEn.menuCarta.specialMenu = {
        enabled: enabledStr,
        title: titleEn,
        description: descriptionEn,
        buttonText: buttonTextEn,
        imageUrl: imageUrlEn,
      }

      await saveTranslations("it", updatedIt)
      await saveTranslations("en", updatedEn)

      i18n.addResourceBundle("it", "global", updatedIt, true, true)
      i18n.addResourceBundle("en", "global", updatedEn, true, true)

      setItTranslations(updatedIt)
      setEnTranslations(updatedEn)

      setMessage({ type: "success", text: "Special menu saved successfully! Reloading..." })
      setTimeout(() => window.location.reload(), 2000)
    } catch (error) {
      console.error("[SpecialMenuEditor] Save error:", error)
      setMessage({ type: "error", text: "Failed to save: " + error.message })
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading special menu...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
      <div className="p-3 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading text-secondary-200">Special Menu Editor</h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              Manage the special menu banner on the Menu Carta page.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-colors ${
              isSaving
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-secondary-200 text-white hover:bg-secondary-400"
            }`}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-4 p-3 rounded-lg ${
              message.type === "success"
                ? "bg-green-100 text-green-700"
                : message.type === "error"
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${enabled ? "bg-green-500" : "bg-gray-300"}`}></div>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : ""}`}></div>
            </div>
            <span className="font-medium text-gray-700">
              Special Menu {enabled ? (
                <span className="text-green-600 text-sm font-normal">(Visible on page)</span>
              ) : (
                <span className="text-gray-400 text-sm font-normal">(Hidden from page)</span>
              )}
            </span>
          </label>
        </div>

        {/* Title */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Title</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">IT</span>
                  Italian
                </span>
              </label>
              <input
                type="text"
                value={titleIt}
                onChange={(e) => setTitleIt(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                placeholder="Titolo del menu speciale..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">EN</span>
                  English
                </span>
              </label>
              <input
                type="text"
                value={titleEn}
                onChange={(e) => setTitleEn(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                placeholder="Special menu title..."
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Description</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">IT</span>
                  Italian
                </span>
              </label>
              <textarea
                value={descriptionIt}
                onChange={(e) => setDescriptionIt(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none text-sm"
                rows={3}
                placeholder="Descrizione del menu speciale..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">EN</span>
                  English
                </span>
              </label>
              <textarea
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none text-sm"
                rows={3}
                placeholder="Special menu description..."
              />
            </div>
          </div>
        </div>

        {/* Button Text */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Button Text</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">IT</span>
                  Italian
                </span>
              </label>
              <input
                type="text"
                value={buttonTextIt}
                onChange={(e) => setButtonTextIt(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                placeholder="Testo del pulsante..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">EN</span>
                  English
                </span>
              </label>
              <input
                type="text"
                value={buttonTextEn}
                onChange={(e) => setButtonTextEn(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                placeholder="Button text..."
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* IT Image */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">IT</span>
                  Italian Image (JPEG)
                </span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/webp"
                onChange={(e) => handleImageUpload(e.target.files[0], "it")}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
              />
              {imageUrlIt && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400 mb-1 truncate">{imageUrlIt}</p>
                  <img
                    src={imageUrlIt}
                    alt="Special Menu IT Preview"
                    className="max-w-full max-h-40 rounded shadow-sm border"
                  />
                </div>
              )}
            </div>

            {/* EN Image */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                <span className="inline-flex items-center gap-1">
                  <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">EN</span>
                  English Image (JPEG)
                </span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/webp"
                onChange={(e) => handleImageUpload(e.target.files[0], "en")}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {imageUrlEn && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400 mb-1 truncate">{imageUrlEn}</p>
                  <img
                    src={imageUrlEn}
                    alt="Special Menu EN Preview"
                    className="max-w-full max-h-40 rounded shadow-sm border"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* How to use */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 mb-2">How to use:</h3>
          <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
            <li>Toggle the switch to show/hide the special menu on the Menu Carta page</li>
            <li>Fill in the title, description, and button text for both Italian and English</li>
            <li>Upload JPEG images for each language (files are named special-menu.jpeg and special-menu-en.jpeg)</li>
            <li>Click "Save Changes" to update both languages on the server</li>
            <li>The special menu appears as a featured banner at the top of the Menu Carta page</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

export default SpecialMenuEditor
